/**
 * Consult Door A — the team-only "Book for client" route.
 *
 *   /book-consult?contact=<cid>
 *
 * The real slot-picker (`BookConsultPanel`) already lives inside the Lead
 * record ( /leads/:id ). This route is the STANDALONE address the Twenty CRM
 * "Book for client" button opens to (roadmap #8, Door A): it reads the
 * `?contact=<cid>` query param, loads the `clients/{cid}` master via the
 * existing `getClient` seam, adapts it to the panel's loose `ConsultLeadLike`
 * shape, and mounts the SAME live `BookConsultPanel` (real Google Calendar +
 * Meet + confirmations via `createConsultBooking`). It does NOT modify the
 * panel or the backend — only mounts it.
 *
 * Guarded admin/staff-only in App.tsx (RequireAuth roles={['admin','staff']},
 * inside AppShell — same nesting as /consultations). Door A live booking also
 * ships behind the shared `consulting` flag (features/consulting/lib/flags),
 * exactly as the /leads/:id "Book" tab does; when the flag is OFF for the
 * signed-in user this route shows an honest "live booking is off" note rather
 * than a dead panel (house rule: no dead controls).
 *
 * States handled, never a blank screen: missing/invalid `?contact`, loading,
 * client-not-found, flag-off, and the mounted panel.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../lib/auth';
import { getClient, type LiveClient } from '../../../lib/projectStore';
import { subscribeConsultingFlag } from '../lib/flags';
import {
  BookConsultPanel,
  type ConsultLeadLike,
} from '../components/BookConsultPanel';

/** Adapt a `clients/{cid}` master to the panel's loose lead-shaped input. */
function clientToConsultLead(client: LiveClient): ConsultLeadLike {
  const city = client.location || client.address?.city || null;
  return {
    id: client.cid,
    name: client.name,
    city,
  };
}

/** Shared page-shell tokens (design system) for the standalone Door-A frame. */
const shellStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: 'var(--s-4, 16px)',
};
const noteStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--ink-mute, #6B7280)',
  border: '1px dashed var(--rule, #E5E7EB)',
  borderRadius: 'var(--r-md, 13px)',
  padding: 'var(--s-5)',
  lineHeight: 1.5,
  overflowWrap: 'anywhere',
};

export default function BookForClientRoute() {
  const [sp] = useSearchParams();
  const contactId = (sp.get('contact') ?? '').trim();
  const { user } = useAuth();

  // Door A ships behind the shared `consulting` flag — mirror /leads/:id.
  const [consultingEnabled, setConsultingEnabled] = useState(false);
  useEffect(
    () => subscribeConsultingFlag(user?.uid ?? null, setConsultingEnabled),
    [user?.uid],
  );

  // undefined = still loading; null = looked, not found.
  const [client, setClient] = useState<LiveClient | null | undefined>(undefined);

  useEffect(() => {
    if (!contactId) return; // handled by the missing-param branch below
    let alive = true;
    setClient(undefined);
    void getClient(contactId)
      .then((c) => {
        if (alive) setClient(c);
      })
      .catch(() => {
        if (alive) setClient(null);
      });
    return () => {
      alive = false;
    };
  }, [contactId]);

  // 1) No / invalid contact param.
  if (!contactId) {
    return (
      <div style={shellStyle}>
        <div style={noteStyle} role="status">
          No client selected. Open this page with a client id in the link, e.g.{' '}
          <code>/book-consult?contact=C00407</code>. From the CRM, use the
          <b> Book for client</b> button on a contact.
        </div>
      </div>
    );
  }

  // 2) Door A flag off for this account — honest, not a dead panel.
  if (!consultingEnabled) {
    return (
      <div style={shellStyle}>
        <div style={noteStyle} role="status">
          Live consult booking (Door A) is off for your account. Ask an admin
          to enable the <b>consulting</b> flag, or add <code>?consult=1</code>{' '}
          to the address to preview it in this browser.
        </div>
      </div>
    );
  }

  // 3) Loading the client master.
  if (client === undefined) {
    return (
      <div style={shellStyle}>
        <div style={noteStyle} role="status" aria-live="polite">
          Loading client {contactId}…
        </div>
      </div>
    );
  }

  // 4) Client not found.
  if (client === null) {
    return (
      <div style={shellStyle}>
        <div style={noteStyle} role="alert">
          No client found for <b>{contactId}</b>. Check the link, or open the
          contact in the portal and use the Book tab there.
        </div>
      </div>
    );
  }

  // 5) Mount the live Door-A panel.
  return (
    <div style={shellStyle}>
      <BookConsultPanel
        lead={clientToConsultLead(client)}
        hostName="Rohit"
        {...(user?.displayName ? { bookedByName: user.displayName } : {})}
      />
    </div>
  );
}
